import React, { useState, forwardRef, useImperativeHandle } from "react";
import { joinWaitlist } from "../lib/api";
import { inr } from "../lib/format";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldCheck } from "lucide-react";

const PLAN_LABEL = { per_case: "Pay-per-case", growth: "Growth", unlimited: "Unlimited" };

export const Waitlist = forwardRef(({ onJoined }, ref) => {
  const [form, setForm] = useState({
    business_name: "", email: "", udyam: "", overdue_amount: "", phone: "", plan_interest: "growth",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useImperativeHandle(ref, () => ({
    setPlan: (p) => setForm((f) => ({ ...f, plan_interest: p })),
  }));

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.udyam || form.overdue_amount === "") {
      toast.error("Please fill email, Udyam number and overdue amount.");
      return;
    }
    setLoading(true);
    try {
      const res = await joinWaitlist({
        ...form,
        overdue_amount: Number(form.overdue_amount),
      });
      setDone(res);
      if (res.status === "already_registered") {
        toast.info(res.message);
      } else {
        toast.success(`You're #${res.position} on the waitlist.`);
      }
      onJoined && onJoined();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" className="border-b border-border paper-noise">
      <div className="max-w-6xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-signal">
            Founding waitlist
          </span>
          <h2 className="mt-3 font-serif text-4xl font-semibold tracking-tight text-ink">
            Get early access &amp; founding pricing
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Baaki is in Phase 1 (live demo). Join the waitlist and we'll onboard you the
            moment the real recovery engine goes live — with locked founding rates.
          </p>
          <ul className="mt-6 space-y-3">
            {["Priority onboarding for Udyam-registered MSMEs", "Founding pricing locked for life", "First access to the Legal Desk"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-sm text-ink">
                <CheckCircle2 className="h-4 w-4 text-success" strokeWidth={1.75} /> {t}
              </li>
            ))}
          </ul>
          <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
            DPDP Act — data captured only to contact you about Baaki.
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="bg-white border border-border doc-shadow p-8">
            {done ? (
              <div data-testid="waitlist-success" className="text-center py-8">
                <CheckCircle2 className="h-12 w-12 text-success mx-auto" strokeWidth={1.25} />
                <h3 className="mt-4 font-serif text-3xl font-semibold text-ink">
                  {done.status === "already_registered" ? "Already on the list" : "You're on the list"}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">{done.message}</p>
                {done.position && (
                  <div className="mt-5 inline-flex items-center gap-3 border border-border px-5 py-3">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Your position</span>
                    <span className="font-mono text-2xl font-semibold text-signal">#{done.position}</span>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={submit} data-testid="waitlist-form" className="space-y-5">
                <Field label="Business name (optional)">
                  <input data-testid="wl-business" value={form.business_name} onChange={set("business_name")}
                    className="baaki-input" placeholder="e.g. Nandi Precision Works" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Business email *">
                    <input data-testid="wl-email" type="email" value={form.email} onChange={set("email")}
                      className="baaki-input" placeholder="you@company.in" required />
                  </Field>
                  <Field label="Phone (optional)">
                    <input data-testid="wl-phone" value={form.phone} onChange={set("phone")}
                      className="baaki-input" placeholder="+91 ..." />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Udyam registration no. *" hint="Format: UDYAM-XX-00-0000000">
                    <input data-testid="wl-udyam" value={form.udyam} onChange={set("udyam")}
                      className="baaki-input font-mono" placeholder="UDYAM-KR-03-0001234" required />
                  </Field>
                  <Field label="Rough overdue amount (₹) *">
                    <input data-testid="wl-amount" type="number" min="0" value={form.overdue_amount} onChange={set("overdue_amount")}
                      className="baaki-input font-mono" placeholder="450000" required />
                  </Field>
                </div>
                <Field label="Plan you're interested in">
                  <div className="flex flex-wrap gap-2" data-testid="wl-plan-group">
                    {Object.keys(PLAN_LABEL).map((p) => (
                      <button type="button" key={p} data-testid={`wl-plan-${p}`}
                        onClick={() => setForm((f) => ({ ...f, plan_interest: p }))}
                        className={`px-4 py-2 text-sm border transition-colors duration-150 ${
                          form.plan_interest === p ? "bg-ink text-white border-ink" : "bg-white text-ink border-border hover:bg-secondary"
                        }`}>
                        {PLAN_LABEL[p]}
                      </button>
                    ))}
                  </div>
                </Field>

                {form.overdue_amount && Number(form.overdue_amount) > 0 && (
                  <div className="bg-secondary border border-border px-4 py-3 text-sm">
                    Estimated statutory interest at ~20% p.a. on {inr(Number(form.overdue_amount))}:{" "}
                    <span className="font-mono text-signal font-medium">
                      ~{inr(Math.round(Number(form.overdue_amount) * 0.2))}/yr
                    </span>
                  </div>
                )}

                <button data-testid="wl-submit" type="submit" disabled={loading}
                  className="w-full bg-signal text-white font-medium text-sm py-3.5 hover:bg-signal-dark hover:-translate-y-[1px] transition-transform duration-150 disabled:opacity-60 flex items-center justify-center gap-2">
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {loading ? "Joining..." : "Join the waitlist"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
});

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-muted-foreground">{hint}</span>}
  </label>
);
